import { create } from 'zustand';
import { endpoints, type ServerConfig, type User } from '../services/api';
import { switchProfile } from './switchProfile';

export interface ProviderStatus {
  connected: boolean;
  email: string | null;
  /** Microsoft To Do permission granted (accounts connected earlier must reconnect). */
  tasks?: boolean;
  /** Work/school account (SharePoint, shared calendars, Planner available). */
  work?: boolean;
  /** Extra work access (SharePoint, shared files, shared calendars) granted. */
  workAccess?: boolean;
}

interface SessionStore {
  config: ServerConfig | null;
  user: User | null;
  loaded: boolean;
  microsoft: ProviderStatus | null;
  load: () => Promise<void>;
  setUser: (u: User | null) => void;
  refreshMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
}

const offlineConfig: ServerConfig = {
  env: 'offline',
  features: { accounts: false, billing: false, microsoft: false },
  licensePrice: { amount: 1000, currency: 'GBP' },
};

export const useSession = create<SessionStore>()((set, get) => ({
  config: null,
  user: null,
  loaded: false,
  microsoft: null,
  load: async () => {
    const [config, me] = await Promise.allSettled([endpoints.config(), endpoints.me()]);
    const user = me.status === 'fulfilled' ? me.value.user : null;
    // If the session couldn't be checked (offline), keep showing the last account's data.
    if (me.status === 'fulfilled') switchProfile(user?.id);
    set({ config: config.status === 'fulfilled' ? config.value : offlineConfig, user, loaded: true });
    await get().refreshMicrosoft();
  },
  setUser: (user) => {
    // Swap to this account's local data before anything (e.g. settings sync) sees the user.
    switchProfile(user?.id);
    set({ user });
    void get().refreshMicrosoft();
  },
  refreshMicrosoft: async () => {
    const { user, config } = get();
    if (!user || !config?.features.microsoft) {
      set({ microsoft: null });
      return;
    }
    try {
      set({ microsoft: await endpoints.microsoftStatus() });
    } catch {
      set({ microsoft: null });
    }
  },
  signOut: async () => {
    await endpoints.logout().catch(() => undefined);
    switchProfile(null);
    set({ user: null, microsoft: null });
  },
}));

export const isLicensed = (u: User | null): boolean => u?.license === 'lifetime';

/**
 * Starts the Microsoft OAuth flow (full-page redirect). `returnTo` is an app path, e.g. /pair.
 * `work` (link only) also asks a work/school account for SharePoint, shared files and
 * shared calendars.
 */
export function startMicrosoft(mode: 'signin' | 'link', returnTo?: string, work = false): void {
  const q = new URLSearchParams({ mode });
  if (returnTo) q.set('returnTo', returnTo);
  if (work && mode === 'link') q.set('access', 'work');
  window.location.assign(`/api/auth/oauth/microsoft/start?${q}`);
}
